import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Chip } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import columnLogoWhite from '~/assets/column-logo-white.svg';
import columnSymbolWhite from '~/assets/column-symbol-white.svg';
import { CardAccountCard, CardProgramCard, CopyInput, EntityCard, Section } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { CardRepository } from '~/repositories/CardRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { formatDateTime } from '~/util';

import { CardProps } from './Card';

interface Params {
  id: string;
}

const cardQueryKey = (id: string) => ['card', id] as const;
const CARD_FONT_STACK = '"Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const StatusChip = styled(Chip)<{ $status: string }>`
  text-transform: capitalize;
`;

const TypeChip = styled(Chip)`
  text-transform: capitalize;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  align-items: start;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.secondary.blendToBackground(100)};

  &:last-child {
    border-bottom: none;
  }
`;

const FieldLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};
`;

const FieldValue = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.foreground};
`;

const FieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const RevealContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 260px;
`;

const RevealCard = styled.div`
  position: relative;
  width: 320px;
  height: 180px;
  border-radius: 12px;
  background: #1e4199;
  color: white;
  font-family: ${CARD_FONT_STACK};
  overflow: hidden;
  box-shadow: 0 10px 28px rgba(10, 10, 10, 0.22);
`;

const RevealBrandSymbol = styled.img`
  position: absolute;
  right: 20px;
  top: 20px;
  width: 16px;
  height: 16px;
  object-fit: contain;
`;

const RevealBrandLogo = styled.img`
  position: absolute;
  top: 20px;
  left: 20px;
  width: 80px;
  height: 18px;
  object-fit: contain;
`;

const RevealCardholderValue = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  bottom: 20px;
  left: 20px;
  width: 180px;
  height: 18px;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity 120ms ease;

  iframe {
    width: 180px;
    height: 18px;
    border: 0;
    background: transparent;
  }
`;

const RevealExpires = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  right: 12px;
  bottom: 42px;
  display: flex;
  align-items: center;
  gap: 0;
  font-family: ${CARD_FONT_STACK};
  font-size: 12px;
  text-transform: uppercase;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  color: ${({ $isVisible }) => ($isVisible ? 'rgba(255, 255, 255, 0.96)' : 'rgba(255, 255, 255, 0.62)')};
  text-shadow: ${({ $isVisible }) => ($isVisible ? '0 0 8px rgba(255, 255, 255, 0.32)' : 'none')};
  transition:
    opacity 120ms ease,
    color 120ms ease,
    text-shadow 120ms ease;
`;

const RevealMetaLabel = styled.span`
  display: inline-block;
  width: 44px;
`;

const RevealExpValue = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 52px;
  gap: 2px;
`;

const RevealExpMonthValue = styled.span<{ $isVisible: boolean }>`
  display: inline-block;
  width: 16px;
  height: 18px;
  vertical-align: middle;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity 120ms ease;

  iframe {
    width: 16px;
    height: 18px;
    border: 0;
    background: transparent;
  }
`;

const RevealExpYearValue = styled.span<{ $isVisible: boolean }>`
  display: inline-block;
  width: 16px;
  height: 18px;
  vertical-align: middle;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity 120ms ease;

  iframe {
    width: 16px;
    height: 18px;
    border: 0;
    background: transparent;
  }
`;

const RevealCvvValue = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  right: 12px;
  bottom: 20px;
  height: 18px;
  display: flex;
  align-items: center;
  gap: 0;
  font-family: ${CARD_FONT_STACK};
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  color: ${({ $isVisible }) => ($isVisible ? 'rgba(255, 255, 255, 0.96)' : 'rgba(255, 255, 255, 0.62)')};
  text-shadow: ${({ $isVisible }) => ($isVisible ? '0 0 8px rgba(255, 255, 255, 0.32)' : 'none')};
  transition:
    opacity 120ms ease,
    color 120ms ease,
    text-shadow 120ms ease;

  iframe {
    width: 52px;
    height: 18px;
    border: 0;
    background: transparent;
  }
`;

const RevealValue = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: 78px;
  left: 20px;
  width: 280px;
  height: 24px;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity 120ms ease;

  iframe {
    width: 280px;
    height: 24px;
    border: 0;
    background: transparent;
  }
`;

const RevealOverlayButton = styled.button`
  position: absolute;
  inset: 0;
  z-index: 2;
  border: none;
  background: rgba(0, 0, 0, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.92);
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.2px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
  cursor: pointer;
  transition: background-color 0.16s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.28);
  }
`;

const formatStatus = (status: string): string => {
  if (!status) return '-';
  return status.toLowerCase().replace(/_/g, ' ');
};

const VGS_SHOW_SCRIPT_URL = 'https://js.verygoodvault.com/vgs-show/2.2.2/show.js';
const VGS_SANDBOX_VAULT_ID = import.meta.env.VITE_CARD_REVEAL_VAULT_ID || 'tntkitewl2g';
const VGS_REVEAL_CNAME = 'reveal.pci-sandbox.column.com';

type VGSFrame = {
  render: (selector: string, styles?: Record<string, string>) => void;
  retry: () => void;
  unmount: () => void;
  on?: (eventName: string, callback: (payload?: unknown) => void) => void;
};

type VGSShowInstance = {
  setEnvironment: (environment: 'sandbox' | 'live') => void;
  setCname?: (cname: string) => void;
  request: (request: Record<string, unknown>) => VGSFrame;
  SERIALIZERS: {
    replace: (pattern: string, replacement: string) => unknown;
  };
};

type VGSShowFactory = {
  create: (vaultId: string, callback?: (state: unknown) => void) => VGSShowInstance;
};

declare global {
  interface Window {
    VGSShow?: VGSShowFactory;
  }
}

let vgsScriptPromise: Promise<void> | null = null;

const loadVgsScript = (): Promise<void> => {
  if (window.VGSShow) {
    return Promise.resolve();
  }

  if (vgsScriptPromise) {
    return vgsScriptPromise;
  }

  vgsScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = VGS_SHOW_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load VGS Show.js'));
    document.body.appendChild(script);
  });

  return vgsScriptPromise;
};

const formatType = (type: string): string => {
  if (!type) return '-';
  return type.toLowerCase().replace(/_/g, ' ');
};

export const PageCardInformation: React.FC = () => {
  const { addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { isSandbox } = useSessionStore();
  const { id } = useParams<keyof Params>() as Params;
  const { card: outletCard } = useOutletContext<CardProps>();
  const [isRevealPending, setIsRevealPending] = useState(false);
  const [isRevealMounted, setIsRevealMounted] = useState(false);
  const [isRevealReady, setIsRevealReady] = useState(false);
  const panRevealFrameRef = useRef<VGSFrame | null>(null);
  const cvvRevealFrameRef = useRef<VGSFrame | null>(null);
  const cardholderRevealFrameRef = useRef<VGSFrame | null>(null);
  const expMonthRevealFrameRef = useRef<VGSFrame | null>(null);
  const expYearRevealFrameRef = useRef<VGSFrame | null>(null);
  const revealInFlightRef = useRef(false);

  const { data: card, error: cardError } = useQuery({
    queryKey: cardQueryKey(id!),
    queryFn: () => CardRepository.get(id!),
    enabled: !!id && !outletCard,
    initialData: outletCard,
  });

  useEffect(() => {
    if (cardError) {
      navigate(ROUTE.CARDS);
      addDangerNotification({
        content: (cardError as any).message || 'Failed to load card',
      });
    }
  }, [cardError, navigate, addDangerNotification]);

  const panContainerId = useMemo(() => `card-pan-reveal-${card?.id || 'unknown'}`, [card?.id]);
  const cvvContainerId = useMemo(() => `card-cvv-reveal-${card?.id || 'unknown'}`, [card?.id]);
  const cardholderContainerId = useMemo(() => `card-cardholder-reveal-${card?.id || 'unknown'}`, [card?.id]);
  const expMonthContainerId = useMemo(() => `card-exp-month-reveal-${card?.id || 'unknown'}`, [card?.id]);
  const expYearContainerId = useMemo(() => `card-exp-year-reveal-${card?.id || 'unknown'}`, [card?.id]);

  useEffect(
    () => () => {
      panRevealFrameRef.current?.unmount();
      cvvRevealFrameRef.current?.unmount();
      cardholderRevealFrameRef.current?.unmount();
      expMonthRevealFrameRef.current?.unmount();
      expYearRevealFrameRef.current?.unmount();
    },
    []
  );

  if (!card) {
    return null;
  }

  const clearRevealFrames = () => {
    panRevealFrameRef.current?.unmount();
    cvvRevealFrameRef.current?.unmount();
    cardholderRevealFrameRef.current?.unmount();
    expMonthRevealFrameRef.current?.unmount();
    expYearRevealFrameRef.current?.unmount();
    const panContainer = document.getElementById(panContainerId);
    const cvvContainer = document.getElementById(cvvContainerId);
    const cardholderContainer = document.getElementById(cardholderContainerId);
    const expMonthContainer = document.getElementById(expMonthContainerId);
    const expYearContainer = document.getElementById(expYearContainerId);
    if (panContainer) panContainer.innerHTML = '';
    if (cvvContainer) cvvContainer.innerHTML = '';
    if (cardholderContainer) cardholderContainer.innerHTML = '';
    if (expMonthContainer) expMonthContainer.innerHTML = '';
    if (expYearContainer) expYearContainer.innerHTML = '';
    panRevealFrameRef.current = null;
    cvvRevealFrameRef.current = null;
    cardholderRevealFrameRef.current = null;
    expMonthRevealFrameRef.current = null;
    expYearRevealFrameRef.current = null;
    setIsRevealPending(false);
    setIsRevealMounted(false);
    setIsRevealReady(false);
  };

  const handleRevealPAN = async () => {
    if (!isSandbox) {
      return;
    }
    if (revealInFlightRef.current) {
      return;
    }

    try {
      clearRevealFrames();
      revealInFlightRef.current = true;
      setIsRevealPending(true);
      setIsRevealReady(false);
      const revealToken = await CardRepository.createRevealToken(card.id);
      await loadVgsScript();

      if (!window.VGSShow) {
        throw new Error('VGS Show.js failed to initialize');
      }

      const show = window.VGSShow.create(VGS_SANDBOX_VAULT_ID, () => undefined);
      show.setEnvironment('sandbox');
      show.setCname?.(VGS_REVEAL_CNAME);

      const revealRequestId = Date.now();

      const panFrame = show.request({
        name: `card-pan-${card.id}-${revealRequestId}`,
        method: 'GET',
        path: `/issuing/cards/${card.id}/reveal`,
        headers: {
          Authorization: `Bearer ${revealToken.id}`,
        },
        htmlWrapper: 'text',
        serializers: [show.SERIALIZERS.replace('(\\S{4})(?=\\S)', '$1 ')],
        jsonPathSelector: 'pan',
      });

      const cvvFrame = show.request({
        name: `card-cvv-${card.id}-${revealRequestId}`,
        method: 'GET',
        path: `/issuing/cards/${card.id}/reveal`,
        headers: {
          Authorization: `Bearer ${revealToken.id}`,
        },
        htmlWrapper: 'text',
        serializers: [show.SERIALIZERS.replace('(\\S)(?=\\S)', '$1')],
        jsonPathSelector: 'cvv2',
      });

      const cardholderFrame = show.request({
        name: `card-cardholder-${card.id}-${revealRequestId}`,
        method: 'GET',
        path: `/issuing/cards/${card.id}/reveal`,
        headers: {
          Authorization: `Bearer ${revealToken.id}`,
        },
        htmlWrapper: 'text',
        jsonPathSelector: 'cardholder_name',
      });

      const expMonthFrame = show.request({
        name: `card-exp-month-${card.id}-${revealRequestId}`,
        method: 'GET',
        path: `/issuing/cards/${card.id}/reveal`,
        headers: {
          Authorization: `Bearer ${revealToken.id}`,
        },
        htmlWrapper: 'text',
        jsonPathSelector: 'expiration_date.month',
      });

      const expYearFrame = show.request({
        name: `card-exp-year-${card.id}-${revealRequestId}`,
        method: 'GET',
        path: `/issuing/cards/${card.id}/reveal`,
        headers: {
          Authorization: `Bearer ${revealToken.id}`,
        },
        htmlWrapper: 'text',
        serializers: [show.SERIALIZERS.replace('(\\d{2})(\\d{2})', '$2')],
        jsonPathSelector: 'expiration_date.year',
      });

      panFrame.render(`#${panContainerId}`, {
        'font-family': CARD_FONT_STACK,
        fontSize: '19px',
        fontWeight: '500',
        lineHeight: '24px',
        letterSpacing: '1px',
        whiteSpace: 'pre',
        color: 'white',
        display: 'block',
        background: 'transparent',
      });

      cvvFrame.render(`#${cvvContainerId}`, {
        'font-family': CARD_FONT_STACK,
        fontSize: '12px',
        fontWeight: '500',
        lineHeight: '16px',
        color: 'white',
        display: 'block',
        background: 'transparent',
      });

      cardholderFrame.render(`#${cardholderContainerId}`, {
        'font-family': CARD_FONT_STACK,
        fontSize: '12px',
        fontWeight: '500',
        lineHeight: '16px',
        textTransform: 'uppercase',
        color: 'white',
        display: 'block',
        background: 'transparent',
      });

      expMonthFrame.render(`#${expMonthContainerId}`, {
        'font-family': CARD_FONT_STACK,
        fontSize: '12px',
        fontWeight: '500',
        lineHeight: '16px',
        color: 'white',
        display: 'block',
        background: 'transparent',
      });

      expYearFrame.render(`#${expYearContainerId}`, {
        'font-family': CARD_FONT_STACK,
        fontSize: '12px',
        fontWeight: '500',
        lineHeight: '16px',
        color: 'white',
        display: 'block',
        background: 'transparent',
      });

      panRevealFrameRef.current = panFrame;
      cvvRevealFrameRef.current = cvvFrame;
      cardholderRevealFrameRef.current = cardholderFrame;
      expMonthRevealFrameRef.current = expMonthFrame;
      expYearRevealFrameRef.current = expYearFrame;
      setIsRevealMounted(true);
    } catch (error: any) {
      setIsRevealPending(false);
      revealInFlightRef.current = false;
      addDangerNotification({
        content: error?.message || 'Failed to reveal PAN',
      });
    }
  };

  useEffect(() => {
    if (!isRevealMounted) {
      return;
    }

    const revealContainer = document.getElementById(panContainerId);
    const revealCvvContainer = document.getElementById(cvvContainerId);
    const revealCardholderContainer = document.getElementById(cardholderContainerId);
    const revealExpMonthContainer = document.getElementById(expMonthContainerId);
    const revealExpYearContainer = document.getElementById(expYearContainerId);
    if (
      !revealContainer ||
      !revealCvvContainer ||
      !revealCardholderContainer ||
      !revealExpMonthContainer ||
      !revealExpYearContainer
    ) {
      return;
    }

    let panLoaded = false;
    let cvvLoaded = false;
    let cardholderLoaded = false;
    let expMonthLoaded = false;
    let expYearLoaded = false;
    let readyTriggered = false;
    let detach: (() => void) | null = null;
    const markReady = () => {
      if (readyTriggered) {
        return;
      }
      readyTriggered = true;
      setIsRevealPending(false);
      // Give the iframe a brief paint window so PAN appears with the rest of the card details.
      window.setTimeout(() => {
        setIsRevealReady(true);
        revealInFlightRef.current = false;
      }, 1000);
    };
    const maybeReady = () => {
      if (panLoaded && cvvLoaded && cardholderLoaded && expMonthLoaded && expYearLoaded) {
        markReady();
      }
    };

    const attachLoadListener = (iframe: HTMLIFrameElement, onLoad: () => void) => {
      iframe.addEventListener('load', onLoad, { once: true });
      return () => iframe.removeEventListener('load', onLoad);
    };
    const cleanupFns: (() => void)[] = [];

    const onPanLoad = () => {
      panLoaded = true;
      maybeReady();
    };

    const onCvvLoad = () => {
      cvvLoaded = true;
      maybeReady();
    };

    const onCardholderLoad = () => {
      cardholderLoaded = true;
      maybeReady();
    };

    const onExpMonthLoad = () => {
      expMonthLoaded = true;
      maybeReady();
    };

    const onExpYearLoad = () => {
      expYearLoaded = true;
      maybeReady();
    };

    const watchContainer = (container: HTMLElement, onLoad: () => void) => {
      const existingIframe = container.querySelector('iframe');
      if (existingIframe instanceof HTMLIFrameElement) {
        cleanupFns.push(attachLoadListener(existingIframe, onLoad));
        return;
      }

      const observer = new MutationObserver(() => {
        const iframe = container.querySelector('iframe');
        if (iframe instanceof HTMLIFrameElement) {
          cleanupFns.push(attachLoadListener(iframe, onLoad));
          observer.disconnect();
        }
      });

      observer.observe(container, { childList: true, subtree: true });
      cleanupFns.push(() => observer.disconnect());
    };

    watchContainer(revealContainer, onPanLoad);
    watchContainer(revealCvvContainer, onCvvLoad);
    watchContainer(revealCardholderContainer, onCardholderLoad);
    watchContainer(revealExpMonthContainer, onExpMonthLoad);
    watchContainer(revealExpYearContainer, onExpYearLoad);
    detach = () => cleanupFns.forEach((fn) => fn());

    // Safety fallback in case the iframe load event does not fire.
    const fallbackTimer = window.setTimeout(markReady, 7000);

    return () => {
      detach?.();
      window.clearTimeout(fallbackTimer);
    };
  }, [isRevealMounted, panContainerId, cvvContainerId, cardholderContainerId, expMonthContainerId, expYearContainerId]);

  return (
    <Inner p={0}>
      {isSandbox && (
        <Section>
          <Inner px="var(--page-margin)" py={16}>
            <RevealContainer>
              <RevealCard>
                {!isRevealReady && (
                  <RevealOverlayButton
                    type="button"
                    onClick={handleRevealPAN}
                    disabled={isRevealPending || isRevealMounted}
                    aria-label="Reveal card"
                  >
                    {isRevealPending || isRevealMounted ? 'Revealing...' : 'Click to Reveal'}
                  </RevealOverlayButton>
                )}
                <RevealValue id={panContainerId} $isVisible={isRevealReady} />
                <RevealBrandLogo src={columnLogoWhite} alt="Column logo" />
                <RevealBrandSymbol src={columnSymbolWhite} alt="Column symbol" />
                <RevealCardholderValue id={cardholderContainerId} $isVisible={isRevealReady} />
                <RevealExpires $isVisible={isRevealReady}>
                  <RevealMetaLabel>EXP</RevealMetaLabel>
                  <RevealExpValue>
                    <RevealExpMonthValue id={expMonthContainerId} $isVisible={isRevealReady} />
                    <span>/</span>
                    <RevealExpYearValue id={expYearContainerId} $isVisible={isRevealReady} />
                  </RevealExpValue>
                </RevealExpires>
                <RevealCvvValue $isVisible={isRevealReady}>
                  <RevealMetaLabel>CVV2</RevealMetaLabel>
                  <span id={cvvContainerId} />
                </RevealCvvValue>
              </RevealCard>
            </RevealContainer>
          </Inner>
        </Section>
      )}
      <Section headline="Information">
        <Inner px="var(--page-margin)" py={16}>
          <FieldsContainer>
            <FieldRow>
              <FieldLabel>Description</FieldLabel>
              <FieldValue>
                <CopyInput value={card.description || '-'} />
              </FieldValue>
            </FieldRow>

            {card.authorizedUserEntityId && (
              <FieldRow>
                <FieldLabel>Authorized User</FieldLabel>
                <FieldValue>
                  <EntityCard entityId={card.authorizedUserEntityId} />
                </FieldValue>
              </FieldRow>
            )}

            <FieldRow>
              <FieldLabel>Card Program</FieldLabel>
              <FieldValue>
                {card.cardProgramId ? <CardProgramCard cardProgramId={card.cardProgramId} /> : <CopyInput value="-" />}
              </FieldValue>
            </FieldRow>

            {card.cardAccountId && (
              <FieldRow>
                <FieldLabel>Card Account</FieldLabel>
                <FieldValue>
                  <CardAccountCard cardAccountId={card.cardAccountId} />
                </FieldValue>
              </FieldRow>
            )}

            <FieldRow>
              <FieldLabel>BIN</FieldLabel>
              <FieldValue>
                <CopyInput value={card.bin || '-'} isMonospaced />
              </FieldValue>
            </FieldRow>

            <FieldRow>
              <FieldLabel>Last Four Digits</FieldLabel>
              <FieldValue>
                <CopyInput value={card.lastFourDigits || '-'} isMonospaced />
              </FieldValue>
            </FieldRow>

            <FieldRow>
              <FieldLabel>Expires</FieldLabel>
              <FieldValue>
                <CopyInput value={card.expiresAt || '-'} />
              </FieldValue>
            </FieldRow>

            <FieldRow>
              <FieldLabel>Status</FieldLabel>
              <FieldValue>
                <StatusChip $status={card.status || ''}>{formatStatus(card.status || '')}</StatusChip>
              </FieldValue>
            </FieldRow>

            <FieldRow>
              <FieldLabel>Type</FieldLabel>
              <FieldValue>
                <TypeChip>{formatType(card.type || '')}</TypeChip>
              </FieldValue>
            </FieldRow>

            {card.createdAt && (
              <FieldRow>
                <FieldLabel>Created At</FieldLabel>
                <FieldValue>
                  <CopyInput value={formatDateTime(card.createdAt, { timeZoneFormat: 'utc' })} />
                </FieldValue>
              </FieldRow>
            )}
          </FieldsContainer>
        </Inner>
      </Section>
    </Inner>
  );
};
